import { useWarehouse } from "../../warehouse/WarehouseContext";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/useAuth";
import { GetDashboard } from "../../services/DashboardService";
import type { DashboardResponse } from "../../models/Dashboard";
import type { DashboardPeriod, DashboardMetric } from "./DashboardState";
export function useDashboard() {
  const { Warehouse } = useWarehouse();
  const { state: loggedInUser } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<DashboardPeriod>("week");
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const refresh = () => setRevision((x) => x + 1);
  useEffect(() => {
    window.addEventListener("pantrystack:navigation-reselect", refresh);
    window.addEventListener("pantrystack:data-changed", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("pantrystack:navigation-reselect", refresh);
      window.removeEventListener("pantrystack:data-changed", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setData(null);
    void GetDashboard(selectedPeriod, controller.signal, Warehouse?.Code)
      .then((result) => {
        if (!controller.signal.aborted) setData(result);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setError(
            error instanceof Error ? error.message : "Dashboard data is unavailable. Retry loading analytics.",
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [selectedPeriod, revision, Warehouse?.Code]);
  const money = useMemo(
    () =>
      new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: data?.CurrencyCode || "NGN",
        maximumFractionDigits: 0,
      }),
    [data?.CurrencyCode],
  );
  const metrics: DashboardMetric[] = data
    ? [
        metric(
          "Total sales",
          money.format(data.SalesTotal),
          data.SalesChange,
          "fa-naira-sign",
          "green",
        ),
        metric(
          "Orders",
          String(data.OrderCount),
          data.OrdersChange,
          "fa-cart-shopping",
          "blue",
        ),
        metric(
          "Buying customers",
          String(data.CustomerCount),
          data.CustomersChange,
          "fa-users",
          "purple",
        ),
        metric(
          "Low stock items",
          String(data.LowStockCount),
          null,
          "fa-triangle-exclamation",
          "orange",
        ),
      ]
    : [];
  return {
    state: { selectedPeriod },
    snapshot: {
      metrics,
      sales: (data?.Sales ?? []).map((x) => ({
        label: x.Label,
        value: x.Value,
      })),
    },
    recentOrders: (data?.RecentOrders ?? []).map((x) => ({
      code: x.Code,
      customer: x.Customer,
      amount: new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: x.CurrencyCode,
      }).format(x.Amount),
      status: x.Status,
      time: new Date(x.OrderedAt).toLocaleString("en-NG"),
    })),
    lowStockItems: (data?.LowStockItems ?? []).map((x) => ({
      code: x.Code,
      name: x.Name,
      detail: `${x.WareHouse || "Unassigned warehouse"} · reorder at ${x.Threshold}`,
      remaining: x.AvailableQuantity,
      icon: "fa-box-open",
    })),
    userFirstName: loggedInUser.User?.Name?.split(" ")[0] ?? "there",
    changePeriod: setSelectedPeriod,
    loading,
    error,
    refresh,
    hasData: data !== null,
    money,
    generatedAt: data?.GeneratedAt,
  };
}
function metric(
  label: string,
  value: string,
  change: number | null,
  icon: string,
  tone: DashboardMetric["tone"],
): DashboardMetric {
  return {
    label,
    value,
    change:
      change === null ? "" : `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`,
    direction: change !== null && change < 0 ? "down" : "up",
    icon,
    tone,
  };
}
